import {
  createContext, useContext, useState, useCallback,
  useEffect, useMemo, useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useDistrict }          from "../lib/district-context";
import { recordEntityInteraction } from "../lib/kevia-memory";
import { pushRuntimeEvent, RT_EVENTS, setState as setRuntimeState } from "../lib/kevia-runtime";
import { persistRuntimeSnapshot, restoreRuntimeSnapshot } from "../lib/kevia-persistence";

const KeviaCtx = createContext(null);

/* ─── Module label map ────────────────────────────────────────────── */

const MODULE_PREFIXES = [
  ["/admin/operations", "operaciones"],
  ["/admin/audit",      "auditoría"],
  ["/admin/users",      "usuarios"],
  ["/admin/review",     "revisión"],
  ["/churches",         "iglesias"],
  ["/families",         "familias"],
  ["/people",           "personas"],
  ["/visits",           "visitas"],
  ["/prayers",          "oraciones"],
  ["/events",           "eventos"],
  ["/preaching",        "predicaciones"],
  ["/materials",        "materiales"],
  ["/notes",            "notas"],
  ["/board",            "junta directiva"],
  ["/kevia",            "kevia"],
];

function deriveModule(pathname) {
  for (const [prefix, label] of MODULE_PREFIXES) {
    if (pathname.startsWith(prefix)) return label;
  }
  return "inicio";
}

/* ─── Provider ────────────────────────────────────────────────────── */

export function KeviaContextProvider({ children }) {
  const [entityContext, setEntityContext] = useState(null);
  // FASE 19: snapshot restoration state (set once on mount, never reset)
  const [continuityState, setContinuityState] = useState(null);

  const { pathname } = useLocation();
  const { userProfile } = useDistrict();

  // Keep a ref to the current pathname so registerContext doesn't need pathname as dep
  const pathnameRef = useRef(pathname);
  useEffect(() => { pathnameRef.current = pathname; }, [pathname]);

  // FASE 19.1–19.2 — Runtime persistence: restore on mount, persist every 5s
  useEffect(() => {
    // Attempt to restore a previous session's runtime state
    const snapshot = restoreRuntimeSnapshot();
    if (snapshot) {
      // Partially rehydrate the store (no timeline restore — events are session-scoped)
      setRuntimeState({
        currentModule: snapshot.currentModule ?? null,
        currentRoute:  snapshot.currentRoute  ?? null,
        activeEntity:  snapshot.activeEntity  ?? null,
        lastAction:    snapshot.lastAction    ?? null,
        recentEvents:  [],
      });
      pushRuntimeEvent({
        type:   RT_EVENTS.RUNTIME_RESTORED,
        module: snapshot.currentModule,
        metadata: { age: Date.now() - snapshot._timestamp, fromSnapshot: true },
      });
      setContinuityState({ age: Date.now() - snapshot._timestamp });
    }

    // Persist compact state every 5 seconds — zero React re-renders
    const timer = setInterval(persistRuntimeSnapshot, 5_000);
    return () => {
      clearInterval(timer);
      persistRuntimeSnapshot(); // final persist on unmount / tab close
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Emit route cognition events on every navigation
  useEffect(() => {
    const module = deriveModule(pathname);
    pushRuntimeEvent({ type: RT_EVENTS.NAVIGATE,     route: pathname, module });
    pushRuntimeEvent({ type: RT_EVENTS.OPEN_MODULE,  route: pathname, module });
  }, [pathname]);

  const registerContext = useCallback((ctx) => {
    setEntityContext(ctx);
    // Emit entity cognition — this is the basis for future memory retrieval
    if (ctx?.id) {
      pushRuntimeEvent({
        type:       RT_EVENTS.OPEN_ENTITY,
        route:      pathnameRef.current,
        module:     deriveModule(pathnameRef.current),
        entityType: ctx.type,
        entityId:   ctx.id,
        entityName: ctx.name,
      });
    }
  }, []);

  const clearContext = useCallback(() => setEntityContext(null), []);

  // Lightweight app scope — injected into orchestrator for context-aware responses
  const appScope = useMemo(() => ({
    route:       pathname,
    module:      deriveModule(pathname),
    district_id: userProfile?.district_id ?? null,
    church_id:   userProfile?.church_id   ?? null,
    role:        userProfile?.role        ?? null,
    kevia_tier:  userProfile?.kevia_tier  ?? "basic",
    pastorName:  userProfile?.full_name   ?? null,
  }), [
    pathname,
    userProfile?.district_id,
    userProfile?.church_id,
    userProfile?.role,
    userProfile?.kevia_tier,
    userProfile?.full_name,
  ]);

  return (
    <KeviaCtx.Provider value={{ entityContext, registerContext, clearContext, appScope, continuityState }}>
      {children}
    </KeviaCtx.Provider>
  );
}

const EMPTY = {
  entityContext:   null,
  registerContext: () => {},
  clearContext:    () => {},
  appScope:        null,
  continuityState: null,
};

export const useKeviaContext = () => useContext(KeviaCtx) ?? EMPTY;

/**
 * Call this in any detail page to automatically register/clear entity context.
 * Also records entity interaction for pastoral memory continuity.
 *
 * @param {{ type, id, name, tone?, contextBlock } | null} ctx
 */
export function useRegisterKeviaContext(ctx) {
  const { registerContext, clearContext } = useKeviaContext();

  useEffect(() => {
    if (!ctx?.contextBlock) return;
    registerContext(ctx);
    recordEntityInteraction(ctx.type, ctx.id, ctx.name);
    return () => clearContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.contextBlock]);
}
